import { useState, useEffect, useMemo } from "react";
import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import { useClub } from "@/contexts/ClubContext";
import { ClubSwitcher } from "./layout/ClubSwitcher";
import { DesktopNav } from "./layout/DesktopNav";
import { MobileMenu } from "./layout/MobileMenu";
import { SiteFooter } from "./layout/SiteFooter";

export default function Layout() {
  const { activeClub, setActiveClub, clubs } = useClub();
  const [menuOpen, setMenuOpen] = useState(false);
  const [isMemberLoggedIn, setIsMemberLoggedIn] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const accentColor = activeClub ? clubs.find(c => c.id === activeClub)?.accent : "#fbbf24";
  const clubBadges = useMemo(() => clubs.filter(c => !c.isBar), [clubs]);
  const vereine = useMemo(() => clubs.filter(c => !c.isBar && !c.isSpecial), [clubs]);
  const specialClubs = useMemo(() => clubs.filter(c => c.isSpecial && c.id !== 'shop'), [clubs]);
  const activeClubObj = activeClub ? clubs.find(c => c.id === activeClub) : null;

  // Check if member is logged in
  useEffect(() => {
    const checkAuth = () => {
      const token = localStorage.getItem("member_token");
      setIsMemberLoggedIn(!!token);
    };
    checkAuth();
    // Listen for storage changes (login/logout in other tabs)
    window.addEventListener("storage", checkAuth);
    // Check periodically for login state changes
    const interval = setInterval(checkAuth, 1000);
    return () => {
      window.removeEventListener("storage", checkAuth);
      clearInterval(interval);
    };
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-50 w-full border-b border-white/10 bg-background/80 glass-header">
        <div className="flex items-center justify-between px-4 md:px-8 h-16">
          <Link to="/" onClick={() => setActiveClub(null)} data-testid="logo-link"
            className="flex items-center gap-2 flex-shrink-0">
            <img src="/assets/6g1kx668_Niners_Logo_Pfeil_ohne_HG.png"
              alt="Ninersbillard" className="h-10 md:h-12 w-auto object-contain" data-testid="header-logo" />
          </Link>

          <ClubSwitcher clubBadges={clubBadges} activeClub={activeClub}
            setActiveClub={setActiveClub} location={location} navigate={navigate} />

          <DesktopNav vereine={vereine} location={location} accentColor={accentColor}
            isMemberLoggedIn={isMemberLoggedIn} setActiveClub={setActiveClub} />

          <MobileMenu menuOpen={menuOpen} setMenuOpen={setMenuOpen} clubBadges={clubBadges}
            vereine={vereine} specialClubs={specialClubs} activeClub={activeClub}
            setActiveClub={setActiveClub} location={location} accentColor={accentColor}
            isMemberLoggedIn={isMemberLoggedIn} />
        </div>

        {activeClubObj && !location.pathname.startsWith('/club/') && (
          <div className="flex items-center justify-between px-4 py-1.5 border-t border-white/5"
            style={{ backgroundColor: `${activeClubObj.accent}12` }}>
            <span className="text-xs font-medium uppercase tracking-widest"
              style={{ color: activeClubObj.accent }}>
              {activeClubObj.name}
            </span>
            <button onClick={() => setActiveClub(null)} data-testid="clear-club-filter"
              className="text-xs text-muted-foreground hover:text-foreground">
              Filter zurücksetzen
            </button>
          </div>
        )}
      </header>

      <main><Outlet /></main>

      <SiteFooter vereine={vereine} isMemberLoggedIn={isMemberLoggedIn} />
    </div>
  );
}
